import { ApiError, type ProblemDetails } from './problem-details'

type QueryPrimitive = string | number | boolean
type QueryValue = QueryPrimitive | readonly QueryPrimitive[] | null | undefined

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  query?: Record<string, QueryValue>
  csrf?: boolean
}

export interface ApiResponse<T> {
  data: T
  headers: Headers
  status: number
}

type TokenProvider = () => string | undefined
type AuthExpiredHandler = () => void

const configuredBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? ''
let csrfTokenProvider: TokenProvider = () => undefined
let authExpiredHandler: AuthExpiredHandler = () => undefined

export function configureCsrfTokenProvider(provider: TokenProvider): void {
  csrfTokenProvider = provider
}

export function configureAuthExpiredHandler(handler: AuthExpiredHandler): void {
  authExpiredHandler = handler
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = new URL(`${configuredBaseUrl}${normalizedPath}`, window.location.origin)

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue

      const values = Array.isArray(value) ? value : [value]
      for (const item of values) {
        url.searchParams.append(key, String(item))
      }
    }
  }

  return url.toString()
}

function isBodyInit(value: unknown): value is BodyInit {
  return (
    typeof value === 'string' ||
    value instanceof Blob ||
    value instanceof FormData ||
    value instanceof URLSearchParams ||
    value instanceof ArrayBuffer ||
    ArrayBuffer.isView(value) ||
    value instanceof ReadableStream
  )
}

function retryAfterSeconds(response: Response): number | undefined {
  const rawValue = response.headers.get('Retry-After')
  if (!rawValue) return undefined

  const seconds = Number(rawValue)
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds)

  const retryAt = Date.parse(rawValue)
  if (Number.isNaN(retryAt)) return undefined
  return Math.max(0, Math.ceil((retryAt - Date.now()) / 1000))
}

async function parseResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined

  const text = await response.text()
  if (!text) return undefined

  const contentType = response.headers.get('Content-Type') ?? ''
  if (contentType.includes('application/json') || contentType.includes('+json')) {
    try {
      return JSON.parse(text) as unknown
    } catch {
      throw new ApiError('服务器返回了无法解析的 JSON。', response.status)
    }
  }

  return text
}

export async function apiRequestWithMeta<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  const { body: inputBody, query, csrf, ...fetchOptions } = options
  const method = (fetchOptions.method ?? 'GET').toUpperCase()
  const headers = new Headers(fetchOptions.headers)
  headers.set('Accept', 'application/json')

  let requestBody: BodyInit | null | undefined
  if (inputBody !== undefined) {
    if (isBodyInit(inputBody)) {
      requestBody = inputBody
    } else {
      headers.set('Content-Type', 'application/json')
      requestBody = JSON.stringify(inputBody)
    }
  }

  const isMutation = !['GET', 'HEAD', 'OPTIONS'].includes(method)
  if (isMutation && csrf !== false) {
    const csrfToken = csrfTokenProvider()
    if (csrfToken) headers.set('X-CSRF-TOKEN', csrfToken)
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, query), {
      ...fetchOptions,
      method,
      headers,
      ...(requestBody === undefined ? {} : { body: requestBody }),
      credentials: 'include',
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError('无法连接服务器，请检查网络后重试。', 0)
  }

  const parsedBody = await parseResponseBody(response)
  if (!response.ok) {
    if (response.status === 401) authExpiredHandler()
    const problem =
      parsedBody && typeof parsedBody === 'object' ? (parsedBody as ProblemDetails) : undefined
    const message = problem?.title ?? problem?.detail ?? `请求失败（${response.status}）`
    throw new ApiError(message, response.status, problem, retryAfterSeconds(response))
  }

  return {
    data: parsedBody as T,
    headers: response.headers,
    status: response.status,
  }
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const response = await apiRequestWithMeta<T>(path, options)
  return response.data
}
